import type { StatsWindow } from '@onflow/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { VolumeChart } from '../components/VolumeChart.js';
import { useMe } from '../hooks/useAuth.js';
import { useFrequency, usePersonalRecords, useVolume } from '../hooks/useStats.js';
import { formatWeight } from '../lib/format.js';

const WINDOWS: StatsWindow[] = ['4w', '12w', '26w', '52w'];

export function Stats() {
  const [window, setWindow] = useState<StatsWindow>('12w');
  const me = useMe();
  const volume = useVolume(window);
  const frequency = useFrequency(window);
  const records = usePersonalRecords();
  const units = me.data?.profile.units ?? 'KG';
  const loading = volume.isLoading || frequency.isLoading || records.isLoading;
  const error = volume.error ?? frequency.error ?? records.error;
  const totalSessions = frequency.data?.points.reduce((sum, point) => sum + point.workouts, 0) ?? 0;
  const totalVolume = volume.data?.points.reduce((sum, point) => sum + point.totalVolume, 0) ?? 0;

  return (
    <section className="fade-in">
      <div className="page-header">
        <h1 className="page-title">Stats</h1>
        <p className="page-subtitle">Training consistency, volume, and personal records</p>
      </div>

      <div className="filter-chips" aria-label="Stats time window" style={{ marginBottom: 16 }}>
        {WINDOWS.map((item) => (
          <button
            key={item}
            type="button"
            className={`chip ${window === item ? 'active' : ''}`}
            onClick={() => setWindow(item)}
          >
            {item}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="empty-state card">Loading stats…</div>
      ) : error ? (
        <div className="empty-state card" role="alert">
          Couldn&apos;t load stats. Try again shortly.
        </div>
      ) : (
        <>
          <div className="workout-card-stats" style={{ marginBottom: 16 }}>
            <span>{totalSessions} sessions</span>
            <span>{formatWeight(totalVolume, units)} volume</span>
            <span>{records.data?.items.length ?? 0} PRs</span>
          </div>
          {volume.data && (
            <div className="card" style={{ padding: 16, height: 280, marginBottom: 20 }}>
              <h2 style={{ fontSize: 15, margin: '0 0 12px' }}>Weekly volume</h2>
              <VolumeChart data={volume.data} units={units} />
            </div>
          )}
          <div className="card" style={{ padding: 16 }}>
            <h2 style={{ fontSize: 15, margin: '0 0 12px' }}>Personal records</h2>
            {records.data?.items.length ? (
              records.data.items.slice(0, 20).map((record) => (
                <Link
                  key={record.exerciseId}
                  to={`/history/${record.workoutId}`}
                  className="workout-card"
                >
                  <strong>{record.exerciseName ?? record.exerciseId}</strong>
                  <div className="workout-card-stats">
                    <span>{formatWeight(record.estimatedOneRepMax, units)} estimated 1RM</span>
                    <span>
                      {formatWeight(record.bestWeight, units)} × {record.bestReps}
                    </span>
                  </div>
                </Link>
              ))
            ) : (
              <p className="muted">Complete weighted sets to establish your first PR.</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
